import { MapContainer, TileLayer, useMapEvents, useMap } from "react-leaflet";
import ClusterLayer from "./ClusterLayer";
import { VictimDetailPanel } from "./VictimDetailPanel";
import { useMemo, useRef, useEffect, useState } from "react";
import { DRAWER_WIDTH_PX } from "../../../utils/constants";
import { useMarkers } from "../../../hooks/map/useMarkers";
import { useFitToPoints } from "../../../hooks/map/useFitToPoints";
import { useCenterOn } from "../../../hooks/map/useCenterOn";
import { useHighlight } from "../../../hooks/useHighlight";
import styled from "styled-components";
import type { VictimDTO } from "@shared/contracts";
import {
  applyVictimMapFilter,
  type VictimMapFilter,
} from "../../../utils/map/victimMapFilter";

const TILE_URL =
  (process.env.REACT_APP_MAP_TILE_URL || "").trim() ||
  "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";

// Styled component for the map container wrapper
const MapWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

function FitToPoints({ points }: { points: { position: [number, number] }[] }) {
  const drawerOffset = window.innerWidth >= 768 ? DRAWER_WIDTH_PX : 0;
  useFitToPoints(points, drawerOffset);
  return null;
}

function CenterOn({ target }: { target: { lat: number; lng: number } | null }) {
  useCenterOn(target);
  return null;
}

function CoordinatePicker() {
  const [picking, setPicking] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const { markerId } = (e as CustomEvent).detail;
      setPicking(markerId);
    };
    window.addEventListener("pick-coordinate-start", handler);
    return () => window.removeEventListener("pick-coordinate-start", handler);
  }, []);

  const map = useMapEvents({
    click(e) {
      if (picking === null) return;
      window.dispatchEvent(
        new CustomEvent("pick-coordinate-result", {
          detail: { markerId: picking, lat: e.latlng.lat, lng: e.latlng.lng },
        }),
      );
      setPicking(null);
    },
  });

  useEffect(() => {
    const container = map.getContainer();
    container.style.cursor = picking !== null ? "crosshair" : "";
  }, [picking, map]);

  return null;
}

function HighlightCursor() {
  const map = useMap();
  const { isHighlightMode } = useHighlight();

  useEffect(() => {
    const container = map.getContainer();
    container.style.cursor = isHighlightMode ? "crosshair" : "";
  }, [isHighlightMode, map]);

  return null;
}

export default function MapView({
  selectedTarget,
  victimFilter,
}: {
  selectedTarget: { lat: number; lng: number } | null;
  victimFilter: VictimMapFilter;
}) {
  const markers = useMarkers();
  const { highlightedIds, showOnlyHighlighted } = useHighlight();
  const highlightedMarkers = useMemo(
    () =>
      showOnlyHighlighted && highlightedIds.size > 0
        ? markers.filter(
            (m) =>
              m.payload?.id != null && highlightedIds.has(String(m.payload.id)),
          )
        : markers,
    [markers, showOnlyHighlighted, highlightedIds],
  );
  const memoMarkers = useMemo(
    () => applyVictimMapFilter(highlightedMarkers, victimFilter),
    [highlightedMarkers, victimFilter],
  );
  const persistedOpenIdsRef = useRef<Set<string>>(new Set());

  const [victimPanels, setVictimPanels] = useState<
    Array<{ id: string; victim: VictimDTO; color: string }>
  >([]);

  useEffect(() => {
    const handler = (e: Event) => {
      const { victim, color } = (
        e as CustomEvent<{ victim: VictimDTO; color: string }>
      ).detail;
      const id = victim.id ?? Math.random().toString(36).slice(2);
      setVictimPanels((prev) => {
        if (prev.some((p) => p.id === id)) return prev;
        return [...prev, { id, victim, color }];
      });
    };
    window.addEventListener("open-victim-detail", handler);
    return () => window.removeEventListener("open-victim-detail", handler);
  }, []);

  useEffect(() => {
    const activeIds = new Set(
      memoMarkers
        .map((m) => m.payload?.id)
        .filter((id): id is string => id != null),
    );
    setVictimPanels((prev) => prev.filter((p) => activeIds.has(p.id)));
  }, [memoMarkers]);

  return (
    <MapWrapper>
      <MapContainer
        center={[20, 0]}
        zoom={3}
        minZoom={3}
        style={{ height: "100%", width: "100%" }}
        zoomControl={false}
        worldCopyJump
        closePopupOnClick={false}
        maxBounds={[
          [-85.05112878, -180],
          [85.05112878, 180],
        ]}
        maxBoundsViscosity={1.0}
      >
        <TileLayer
          attribution='&copy; <a href="https://stadiamaps.com/">Stadia Maps</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url={TILE_URL}
          maxZoom={19}
          noWrap
          bounds={[
            [-85.05112878, -180],
            [85.05112878, 180],
          ]}
        />
        <ClusterLayer
          points={memoMarkers}
          size={40}
          persistedOpenIdsRef={persistedOpenIdsRef}
        />
        <FitToPoints points={memoMarkers} />
        <CenterOn target={selectedTarget} />
        <CoordinatePicker />
        <HighlightCursor />
      </MapContainer>

      {victimPanels.map(({ id, victim, color }) => (
        <VictimDetailPanel
          key={id}
          victim={victim}
          color={color}
          onClose={() =>
            setVictimPanels((prev) => prev.filter((p) => p.id !== id))
          }
        />
      ))}
    </MapWrapper>
  );
}
